// src/pages/RiskCalculator.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../auth/AuthContext";
import { toast } from "react-toastify";
import GuestBanner from "../components/GuestBanner";
import { normalizeRisk } from "../lib/normalize";
import { toFloatLoose } from "../lib/parse";
import { trackEvent } from "../lib/trackEvent";

const RiskCalculator = () => {
  const { api, user } = useAuth();
  const isGuest = user?.isGuest || user?.type === "guest";
  const isAuthed = !!user && !isGuest;
  const shouldShowPremiumBanner = !user;
  const RISK_ENGINE_MAX_ALLOWED_RISK = 1;
  const RISK_ENGINE_MIN_RR = 2.0;

  const [form, setForm] = useState({
    ticker: "",
    accountBalance: "",
    riskPercent: "",
    entryPrice: "",
    stopLossPrice: "",
    targetPrice: "",
  });

  const [result, setResult] = useState(null);
  const [savedCalcs, setSavedCalcs] = useState([]);
  const [tempCalcs, setTempCalcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [riskEngineResult, setRiskEngineResult] = useState(null);
  const [riskEngineError, setRiskEngineError] = useState("");
  const [riskEngineLoading, setRiskEngineLoading] = useState(false);
  const [showRiskInfo, setShowRiskInfo] = useState(false);
  const [expandedCalcIds, setExpandedCalcIds] = useState({});

  useEffect(() => {
    trackEvent("risk_page_viewed", "risk_calculator", {
      access_type: isAuthed ? "authenticated" : "guest",
    });

    // Page-view analytics should fire once when this page mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const toggleCalcExpanded = (calcId) => {
    setExpandedCalcIds((prev) => ({
      ...prev,
      [calcId]: !prev[calcId],
    }));
  };

  const toDisplayNumber = (value) => {
    if (value == null || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatMoney = (value) => {
    const parsed = toDisplayNumber(value);
    return parsed != null ? `$${parsed}` : "-";
  };

  const formatPlainNumber = (value) => {
    const parsed = toDisplayNumber(value);
    return parsed != null ? parsed : "-";
  };

  const formatPercent = (value) => {
    const parsed = toDisplayNumber(value);
    return parsed != null ? `${parsed}%` : "-";
  };

  const getPersistedValue = (calc, camelKey, snakeKey) =>
    calc?.[camelKey] ?? calc?.[snakeKey] ?? null;

  const getStatusDisplay = (statusValue, hasViolations) => {
    if (statusValue === "outside_preferred_rules" || hasViolations) {
      return {
        label: "Outside Preferred Rules",
        className: "text-yellow-400",
      };
    }

    if (statusValue === "within_preferred_rules") {
      return {
        label: "Within Preferred Rules",
        className: "text-green-400",
      };
    }

    return {
      label: "-",
      className: "text-gray-400",
    };
  };

  async function fetchCalcs() {
    try {
      if (!isAuthed) {
        setSavedCalcs([]);
        return;
      }

      const rows = await api("/api/risk", { method: "GET" });
      setSavedCalcs(Array.isArray(rows) ? rows.map(normalizeRisk) : []);
    } catch (err) {
      console.error("Fetch Risk Calcs Error:", err);
      setSavedCalcs([]);
    } finally {
      setLoading(false);
    }
  }

  function buildRiskEngineStorageFields(engineResult) {
    if (!engineResult) return {};

    return {
      riskEngineScore: engineResult.tradeScore ?? null,
      riskEngineRiskPercent: engineResult.metrics?.riskPercent ?? null,
      riskEngineTotalRisk: engineResult.metrics?.totalRisk ?? null,
      riskEngineTotalReward: engineResult.metrics?.totalReward ?? null,
      riskEngineRR: engineResult.metrics?.rr ?? null,
      riskEngineStatus:
        engineResult.violations?.length > 0
          ? "outside_preferred_rules"
          : "within_preferred_rules",
      riskEngineViolations: engineResult.violations ?? [],
    };
  }

  async function calculateRisk() {
    trackEvent("risk_calculation_started", "risk_calculator", {
      access_type: isAuthed ? "authenticated" : "guest",
      has_ticker: Boolean(form.ticker.trim()),
      has_target_price: Boolean(form.targetPrice),
    });

    if (isAuthed) {
      trackEvent("risk_save_attempted", "risk_calculator", {
        access_type: "authenticated",
      });
    }

    setSaving(true);
    setRiskEngineLoading(true);
    setRiskEngineError("");
    setRiskEngineResult(null);
    setShowRiskInfo(false);

    try {
      const payload = {
        ticker: form.ticker.trim(),
        accountBalance: toFloatLoose(form.accountBalance),
        riskPercent: toFloatLoose(form.riskPercent),
        entryPrice: toFloatLoose(form.entryPrice),
        stopLossPrice: toFloatLoose(form.stopLossPrice),
        targetPrice: toFloatLoose(form.targetPrice),
      };

      if (!isAuthed) {
        const calc = {
          id: `temp-${Date.now()}`,
          ticker: payload.ticker,
          balance: payload.accountBalance ?? null,
          riskPct: payload.riskPercent ?? null,
          entry: payload.entryPrice ?? null,
          stop: payload.stopLossPrice ?? null,
          target: payload.targetPrice ?? null,
        };

        const riskAmount =
          calc.balance != null && calc.riskPct != null
            ? +(calc.balance * (calc.riskPct / 100)).toFixed(2)
            : null;

        const stopDist =
          calc.entry != null && calc.stop != null
            ? Math.abs(calc.entry - calc.stop)
            : null;

        const positionSize =
          riskAmount != null && stopDist && stopDist > 0
            ? Math.floor(riskAmount / stopDist)
            : null;

        const rrRatio =
          calc.target != null &&
          calc.entry != null &&
          stopDist &&
          stopDist > 0
            ? +(((calc.target - calc.entry) / stopDist).toFixed(2))
            : null;

        setResult({
          ticker: calc.ticker,
          riskAmount,
          positionSize,
          rrRatio,
        });

        trackEvent("risk_calculation_completed", "risk_calculator", {
          access_type: "guest",
          ticker: calc.ticker || null,
          risk_percent: calc.riskPct,
          rr_ratio: rrRatio,
          position_size: positionSize,
          persisted: false,
        });

        setTempCalcs((prev) => [
          { ...calc, riskAmount, positionSize, rrRatio },
          ...prev,
        ]);

        setRiskEngineLoading(false);

        toast.info(
          "Saved locally for this session only. In guest mode, saved risk calculations will not persist after refresh. Sign up with your email and password to unlock premium persistence and premium features."
        );

        return;
      }

      const savePayload = { ...payload };

      const balance = payload.accountBalance;
      const riskPctDecimal = payload.riskPercent / 100;
      const entry = payload.entryPrice;
      const stop = payload.stopLossPrice;
      const target = payload.targetPrice;

      const riskAmount = balance * riskPctDecimal;
      const riskPerShare = Math.abs(entry - stop);

      const positionSize =
        riskPerShare > 0
          ? Math.floor(riskAmount / riskPerShare)
          : 0;

      const rrRatio =
        target != null && riskPerShare > 0
          ? Number(((target - entry) / riskPerShare).toFixed(2))
          : null;

      try {
        const riskEnginePayload = {
          entryPrice: payload.entryPrice,
          stopPrice: payload.stopLossPrice,
          targetPrice: payload.targetPrice,
          accountSize: payload.accountBalance,
          positionSize,
        };

        const engineResult = await api("/api/risk/evaluate", {
          method: "POST",
          body: JSON.stringify(riskEnginePayload),
        });

        const riskEngineStorageFields =
          buildRiskEngineStorageFields(engineResult);

        Object.assign(savePayload, riskEngineStorageFields);

        setRiskEngineResult(engineResult);
      } catch (engineError) {
        console.error("Risk engine error:", engineError);

        setRiskEngineError(
          engineError.message || "Risk engine evaluation failed"
        );
      }

      const created = await api("/api/risk", {
        method: "POST",
        body: JSON.stringify(savePayload),
      });

      const normalizedCreated = normalizeRisk(created);

      trackEvent("risk_calculation_completed", "risk_calculator", {
        access_type: "authenticated",
        ticker: payload.ticker || null,
        risk_percent: payload.riskPercent,
        rr_ratio: rrRatio,
        position_size: positionSize,
        risk_engine_status:
          savePayload.riskEngineStatus || "evaluation_unavailable",
        risk_engine_score: savePayload.riskEngineScore ?? null,
        persisted: true,
      });

      trackEvent("risk_saved", "risk_calculator", {
        access_type: "authenticated",
        ticker: payload.ticker || null,
        calculation_id:
          normalizedCreated?.id || created?.id || null,
        risk_engine_status:
          savePayload.riskEngineStatus || "evaluation_unavailable",
      });

      setResult({
        ...normalizedCreated,
        riskAmount,
        positionSize,
        rrRatio,
      });

      toast.success("Risk calculation saved!");

      await fetchCalcs();
    } catch (error) {
      console.error(error);

      setResult({
        error: error.message,
      });

      toast.error(error.message);
    } finally {
      setSaving(false);
      setRiskEngineLoading(false);
    }
  }

  useEffect(() => {
    if (!isAuthed) {
      localStorage.removeItem("risk_calculations");

      setSavedCalcs([]);
    }

    setLoading(true);
    fetchCalcs();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, isGuest]);

  const rowsToShow = [...tempCalcs, ...savedCalcs];

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 text-white">
      <GuestBanner page="risk" />

      <h1 className="text-3xl font-bold mb-6 text-center">
        Risk Calculator
      </h1>

      {shouldShowPremiumBanner && (
        <div className="mb-6 rounded-lg border border-blue-500/30 bg-blue-500/10 p-4 text-sm text-blue-100">
          <p className="font-semibold text-white mb-1">
            Premium Access
          </p>

          <p>
            Try our premium Risk Engine to help evaluate your trades. It is
            currently free after signup with your email and password.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          "ticker",
          "accountBalance",
          "riskPercent",
          "entryPrice",
          "stopLossPrice",
          "targetPrice",
        ].map((field) => (
          <input
            key={field}
            type="text"
            name={field}
            placeholder={field.replace(/([A-Z])/g, " $1")}
            value={form[field]}
            onChange={handleChange}
            className="p-3 rounded bg-gray-800 border border-gray-600 placeholder-gray-400"
          />
        ))}
      </div>

      <button
        onClick={calculateRisk}
        disabled={saving}
        className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded"
      >
        {saving ? "Saving…" : "Save Calculation"}
      </button>

      {result && (
        <div className="mt-6 bg-gray-900 p-4 rounded border border-gray-700">
          {result.error ? (
            <p className="text-red-500">{result.error}</p>
          ) : (
            <>
              <p>
                <strong>Ticker:</strong>{" "}
                {result.ticker || form.ticker || "-"}
              </p>

              <p>
                <strong>Risk Amount:</strong>{" "}
                {result.riskAmount != null ? `$${result.riskAmount}` : "-"}
              </p>

              <p>
                <strong>Position Size:</strong>{" "}
                {result.positionSize != null ? result.positionSize : "-"} shares
              </p>

              {result.rrRatio != null && (
                <p>
                  <strong>Reward-to-Risk Ratio:</strong> {result.rrRatio}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {riskEngineLoading && (
        <div className="mt-4 bg-gray-900 p-4 rounded border border-gray-700">
          <p className="text-gray-300">Evaluating trade...</p>
        </div>
      )}

      {riskEngineError && (
        <div className="mt-4 bg-red-900/30 p-4 rounded border border-red-700">
          <p className="text-red-400">{riskEngineError}</p>
        </div>
      )}

      {riskEngineResult && (
        <div className="mt-4 bg-gray-900 p-4 rounded border border-gray-700">
          <h2 className="text-xl font-semibold mb-3">
            Risk Engine Evaluation
          </h2>

          <div className="mb-4">
            <button
              type="button"
              onClick={() => setShowRiskInfo((prev) => !prev)}
              aria-expanded={showRiskInfo}
              className="text-sm text-blue-400 hover:text-blue-300 underline underline-offset-2"
            >
              {showRiskInfo
                ? "Hide evaluation rules"
                : "What does this mean?"}
            </button>

            {showRiskInfo && (
              <div className="mt-3 rounded border border-gray-700 bg-gray-800 p-3 text-sm text-gray-300">
                <p className="font-semibold text-white mb-2">
                  Risk Engine Rules
                </p>

                <p>
                  <strong>Max allowed risk:</strong>{" "}
                  {RISK_ENGINE_MAX_ALLOWED_RISK}% of account balance
                </p>

                <p>
                  <strong>Minimum risk/reward:</strong>{" "}
                  {RISK_ENGINE_MIN_RR}
                </p>

                <p className="mt-2">
                  These warnings do not block the trade. They simply show
                  whether the setup is within or outside the current preferred
                  rule set.
                </p>

                <div className="mt-4">
                  <p className="font-semibold text-white mb-2">
                    Score Guide
                  </p>

                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      <strong>80–100:</strong> Strong setup
                    </li>

                    <li>
                      <strong>60–79:</strong> Decent setup, but review closely
                    </li>

                    <li>
                      <strong>0–59:</strong> Weak setup, likely outside
                      preferred rules
                    </li>
                  </ul>
                </div>

                <div className="mt-4">
                  <p className="font-semibold text-white mb-2">
                    Warning Guide
                  </p>

                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      <strong>Risk exceeds max allowed %:</strong> You are
                      risking more of your account than the preferred threshold.
                    </li>

                    <li>
                      <strong>
                        Risk/Reward below minimum threshold:
                      </strong>{" "}
                      The potential reward is too small relative to the risk.
                    </li>
                  </ul>
                </div>

                <div className="mt-4">
                  <p className="font-semibold text-white mb-2">
                    Risk / Reward Guide
                  </p>

                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      <strong>Risk / Reward:</strong> This compares how much you
                      could potentially make versus how much you are risking on
                      the trade.
                    </li>

                    <li>
                      <strong>Example:</strong> A 2.0 risk/reward means you are
                      targeting $2 of reward for every $1 of risk.
                    </li>

                    <li>
                      Higher risk/reward setups can be more attractive, but
                      they still need to fit your overall trade quality and
                      risk rules.
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          <p>
            <strong>Risk %:</strong>{" "}
            {riskEngineResult.metrics?.riskPercent != null
              ? `${riskEngineResult.metrics.riskPercent}%`
              : "-"}
          </p>

          <p>
            <strong>Total Risk:</strong>{" "}
            {riskEngineResult.metrics?.totalRisk != null
              ? `$${riskEngineResult.metrics.totalRisk}`
              : "-"}
          </p>

          <p>
            <strong>Total Reward:</strong>{" "}
            {riskEngineResult.metrics?.totalReward != null
              ? `$${riskEngineResult.metrics.totalReward}`
              : "-"}
          </p>

          <p>
            <strong>Risk / Reward:</strong>{" "}
            {riskEngineResult.metrics?.rr != null
              ? riskEngineResult.metrics.rr
              : "-"}
          </p>

          <p>
            <strong>Score:</strong>{" "}
            {riskEngineResult.tradeScore != null
              ? riskEngineResult.tradeScore
              : "-"}
          </p>

          {(() => {
            const liveStatus = getStatusDisplay(
              null,
              riskEngineResult.violations?.length > 0
            );

            return (
              <p>
                <strong>Status:</strong>{" "}
                <span className={liveStatus.className}>
                  {liveStatus.label}
                </span>
              </p>
            );
          })()}

          {riskEngineResult.violations?.length > 0 && (
            <div className="mt-3">
              <ul className="list-disc pl-5 text-yellow-300">
                {riskEngineResult.violations.map((violation, index) => (
                  <li key={index}>{violation}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-3">
          Your Saved Calculations
        </h2>

        {loading ? (
          <p className="text-gray-400">Loading…</p>
        ) : rowsToShow.length === 0 ? (
          <p className="text-gray-400 italic">
            No saved calculations yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {rowsToShow.map((calc) => {
              const isTemp = String(calc.id).startsWith("temp-");

              const isExpanded = !!expandedCalcIds[calc.id];

              const persistedScore = getPersistedValue(
                calc,
                "riskEngineScore",
                "risk_engine_score"
              );

              const persistedRiskPercent = getPersistedValue(
                calc,
                "riskEngineRiskPercent",
                "risk_engine_risk_percent"
              );

              const persistedTotalRisk = getPersistedValue(
                calc,
                "riskEngineTotalRisk",
                "risk_engine_total_risk"
              );

              const persistedTotalReward = getPersistedValue(
                calc,
                "riskEngineTotalReward",
                "risk_engine_total_reward"
              );

              const persistedRR = getPersistedValue(
                calc,
                "riskEngineRR",
                "risk_engine_rr"
              );

              const persistedStatus = getPersistedValue(
                calc,
                "riskEngineStatus",
                "risk_engine_status"
              );

              const persistedViolations = getPersistedValue(
                calc,
                "riskEngineViolations",
                "risk_engine_violations"
              );

              const violationsList = Array.isArray(persistedViolations)
                ? persistedViolations
                : typeof persistedViolations === "string"
                ? (() => {
                    try {
                      const parsed = JSON.parse(persistedViolations);

                      return Array.isArray(parsed) ? parsed : [];
                    } catch {
                      return persistedViolations
                        ? [persistedViolations]
                        : [];
                    }
                  })()
                : [];

              const persistedStatusDisplay = getStatusDisplay(
                persistedStatus,
                violationsList.length > 0
              );

              const hasPersistedRiskEngineData =
                persistedScore != null ||
                persistedRiskPercent != null ||
                persistedTotalRisk != null ||
                persistedTotalReward != null ||
                persistedRR != null ||
                persistedStatus != null ||
                violationsList.length > 0;

              return (
                <li
                  key={calc.id}
                  className="p-3 bg-gray-800 rounded border border-gray-700"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p>
                          <strong>{calc.ticker}</strong>{" "}
                          {isTemp && (
                            <span className="text-xs text-gray-400">
                              (local only)
                            </span>
                          )}
                        </p>

                        {!isTemp && hasPersistedRiskEngineData && (
                          <span
                            className={`text-xs font-medium ${persistedStatusDisplay.className}`}
                          >
                            {persistedStatusDisplay.label}
                          </span>
                        )}
                      </div>

                      <div className="mt-2 space-y-1 text-sm text-gray-200">
                        <p>
                          Balance: {formatMoney(calc.balance)}
                        </p>

                        <p>
                          Risk %: {formatPercent(calc.riskPct)}
                        </p>

                        <p>
                          Entry: {formatPlainNumber(calc.entry)} | Stop:{" "}
                          {formatPlainNumber(calc.stop)} | Target:{" "}
                          {formatPlainNumber(calc.target)}
                        </p>

                        <p>
                          Risk Amount: {formatMoney(calc.riskAmount)} | Size:{" "}
                          {formatPlainNumber(calc.positionSize)} | R:R{" "}
                          {formatPlainNumber(calc.rrRatio)}
                        </p>
                      </div>
                    </div>

                    {!isTemp && (
                      <button
                        type="button"
                        onClick={() => toggleCalcExpanded(calc.id)}
                        className="text-sm text-blue-400 hover:text-blue-300 underline underline-offset-2 whitespace-nowrap"
                      >
                        {isExpanded
                          ? "Hide Risk Engine"
                          : "Show Risk Engine"}
                      </button>
                    )}
                  </div>

                  {!isTemp && isExpanded && (
                    <div className="mt-4 rounded border border-gray-700 bg-gray-900 p-3">
                      <h3 className="text-sm font-semibold text-white mb-2">
                        Persisted Risk Engine Details
                      </h3>

                      {hasPersistedRiskEngineData ? (
                        <>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="rounded border border-gray-700 bg-gray-800 p-3">
                              <p className="text-xs uppercase tracking-wide text-gray-400">
                                Score
                              </p>

                              <p className="mt-1 text-lg font-semibold text-white">
                                {formatPlainNumber(persistedScore)}
                              </p>
                            </div>

                            <div className="rounded border border-gray-700 bg-gray-800 p-3">
                              <p className="text-xs uppercase tracking-wide text-gray-400">
                                Status
                              </p>

                              <p
                                className={`mt-1 text-sm font-semibold ${persistedStatusDisplay.className}`}
                              >
                                {persistedStatusDisplay.label}
                              </p>
                            </div>
                          </div>

                          <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-gray-200 sm:grid-cols-2">
                            <p>
                              <strong>Risk %:</strong>{" "}
                              {formatPercent(persistedRiskPercent)}
                            </p>

                            <p>
                              <strong>Risk / Reward:</strong>{" "}
                              {formatPlainNumber(persistedRR)}
                            </p>

                            <p>
                              <strong>Total Risk:</strong>{" "}
                              {formatMoney(persistedTotalRisk)}
                            </p>

                            <p>
                              <strong>Total Reward:</strong>{" "}
                              {formatMoney(persistedTotalReward)}
                            </p>
                          </div>

                          {violationsList.length > 0 ? (
                            <div className="mt-3 rounded border border-yellow-500/30 bg-yellow-500/10 p-3">
                              <p className="font-semibold text-yellow-300 mb-1">
                                Warnings
                              </p>

                              <ul className="list-disc pl-5 text-yellow-200">
                                {violationsList.map((violation, index) => (
                                  <li
                                    key={`${calc.id}-violation-${index}`}
                                  >
                                    {violation}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <p className="mt-3 rounded border border-green-500/30 bg-green-500/10 p-3 text-green-300">
                              No warnings for this saved calculation.
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-gray-400 italic">
                          No persisted Risk Engine data is available for this
                          saved calculation.
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default RiskCalculator;